const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section className="bg-slate-800/60 border border-slate-700 rounded-xl p-4 space-y-2">
    <h2 className="text-sm font-semibold text-emerald-400 uppercase tracking-wide">{title}</h2>
    {children}
  </section>
);

const P = ({ children }: { children: React.ReactNode }) => (
  <p className="text-sm text-slate-300 leading-relaxed">{children}</p>
);

const Steps = ({ items }: { items: string[] }) => (
  <ol className="list-decimal list-inside space-y-1.5">
    {items.map((item, i) => (
      <li key={i} className="text-sm text-slate-300 leading-relaxed">{item}</li>
    ))}
  </ol>
);

const Tips = ({ items }: { items: { title: string; body: string }[] }) => (
  <div className="space-y-3">
    {items.map(({ title, body }) => (
      <div key={title}>
        <div className="text-xs font-semibold text-slate-200 mb-0.5">{title}</div>
        <p className="text-sm text-slate-400 leading-relaxed">{body}</p>
      </div>
    ))}
  </div>
);

export default function HelpTab() {
  return (
    <div className="space-y-4 pb-8">

      <section className="bg-slate-800/60 border border-slate-700 rounded-xl p-4">
        <div className="flex items-baseline gap-2 mb-2">
          <h1 className="text-base font-bold text-white">SweetShot Select™</h1>
          <span className="text-xs text-slate-500">Instructions for Use · v3.0</span>
        </div>
        <P>
          SweetShot Select is a shot-by-shot caddie for all golfers, combining GPS distance,
          live wind, and elevation data to recommend the right club and shot shape on every
          approach. On the greens, it reads the break for you — computing exact aim point,
          break amount, and speed from a pre-mapped slope model of each green. Your bag
          distances live in your profile, so every recommendation is calibrated to your game.
        </P>
      </section>

      <Section title="Setup — do this once">
        <Steps items={[
          "Open the Profile tab.",
          "Select your course from the Course dropdown at the top of the Profile tab.",
          "Enter your carry distance for each club in your bag — use your average carry, not your best hit.",
          "Save your bag. You can update distances anytime your game changes.",
          "Before each round at a different course, return to Profile and switch the course selector.",
        ]} />
      </Section>

      <Section title="Shot flow — approach shots">
        <Steps items={[
          "Open the Shot tab when you are ready to hit.",
          "Confirm the hole number — it advances automatically after each hole.",
          "Set the pin position (front/middle/back and left/center/right) from the daily pin sheet.",
          "The app reads your GPS location, pulls live wind speed and direction, and factors in elevation change and your energy level.",
          "Your club recommendation and shot shape appear on screen.",
          "Hit the shot, then log your execution quality and result.",
          "Tap Next Shot to stay on the same hole, or Next Hole to advance.",
        ]} />
      </Section>

      <Section title="Putt flow — green reading">
        <Steps items={[
          "After reaching the green, tap the Putt tab.",
          "The green diagram for your current hole loads automatically.",
          "Tap the pin location directly on the green image — match it to where the flag is set today.",
          "The app cross-references your GPS position with the pre-mapped slope model for that green.",
          "Your read appears: break direction, break amount in inches, aim point, and speed instruction.",
          "Align your putter to the aim point shown. Trust the read.",
        ]} />
      </Section>

      <Section title="Wind compass">
        <P>
          The wind compass shows three arrows: blue (your shot direction), orange (raw wind from
          the weather source), and green (final shot wind — what the engine actually uses).
          Confirm all three match what you feel on the course. If wind shifts between loading
          and hitting, refresh the recommendation.
        </P>
      </Section>

      <Section title="Tips for best results">
        <Tips items={[
          {
            title: "GPS accuracy",
            body: "Let the app settle for 10–15 seconds before pulling a recommendation, especially on your first hole. Accurate GPS is the foundation of every calculation.",
          },
          {
            title: "Tapping the pin",
            body: "Tap the pin location on the green diagram as precisely as you can. Even a small error shifts your aim point. Use the daily pin sheet and take a moment to place your tap carefully.",
          },
          {
            title: "Wind data",
            body: "Wind is pulled live when you open the Shot tab. If you wait several minutes before hitting, consider refreshing — but maintain pace of play.",
          },
          {
            title: "Club distances",
            body: "The recommendation is only as good as your profile. If you are consistently hitting farther or shorter than stored distances, update your bag. Mid-season is a good time to recalibrate.",
          },
          {
            title: "Green reads",
            body: "The slope model captures the major break well. Always add your own eye as a final check. Commit to the putt.",
          },
          {
            title: "Logging outcomes",
            body: "Consistently logging execution quality and result makes your shot history meaningful. Even a 5-second log per shot adds up to useful data over time.",
          },
        ]} />
      </Section>

      <section className="text-center text-xs text-slate-600 pt-2">
        SweetShot Select™ is a decision-support tool and does not replace player judgment.
      </section>
    </div>
  );
}
